/**
 * Which fields must be filled in before a record can be saved. Every workshop
 * starts from the defaults below and may switch the optional ones on or off
 * (stored in workshops.settings.requiredFields). Locked fields stay required:
 * without them the record makes no sense.
 */
import type { CustomerInput, VehicleInput } from "./masterData";
import type { WorkOrderInput } from "./workOrderRecord";

export type RequiredEntity = "customer" | "vehicle" | "workOrder";

export interface RequiredFieldDefinition {
  entity: RequiredEntity;
  /** Field name; "phoneAny" means phone or mobile. */
  field: string;
  /** i18n key of the label shown in forms and settings. */
  labelKey: string;
  defaultRequired: boolean;
  locked: boolean;
}

export const REQUIRED_FIELDS: readonly RequiredFieldDefinition[] = [
  { entity: "customer", field: "name", labelKey: "customer.name", defaultRequired: true, locked: true },
  { entity: "customer", field: "phoneAny", labelKey: "required.phoneAny", defaultRequired: true, locked: false },
  { entity: "customer", field: "email", labelKey: "customer.email", defaultRequired: false, locked: false },
  { entity: "customer", field: "city", labelKey: "customer.city", defaultRequired: false, locked: false },
  { entity: "customer", field: "addressLine", labelKey: "customer.addressLine", defaultRequired: false, locked: false },
  { entity: "customer", field: "taxNumber", labelKey: "customer.taxNumber", defaultRequired: false, locked: false },
  { entity: "vehicle", field: "plateNumber", labelKey: "vehicle.plateNumber", defaultRequired: true, locked: true },
  { entity: "vehicle", field: "customerId", labelKey: "vehicle.owner", defaultRequired: false, locked: false },
  { entity: "vehicle", field: "make", labelKey: "vehicle.make", defaultRequired: false, locked: false },
  { entity: "vehicle", field: "model", labelKey: "vehicle.model", defaultRequired: false, locked: false },
  { entity: "vehicle", field: "vin", labelKey: "vehicle.vin", defaultRequired: false, locked: false },
  { entity: "vehicle", field: "engineCode", labelKey: "vehicle.engineCode", defaultRequired: false, locked: false },
  { entity: "vehicle", field: "odometerKm", labelKey: "vehicle.odometerKm", defaultRequired: false, locked: false },
  { entity: "workOrder", field: "vehicleId", labelKey: "workOrder.vehicle", defaultRequired: true, locked: true },
  { entity: "workOrder", field: "customerId", labelKey: "workOrder.customer", defaultRequired: true, locked: true },
  { entity: "workOrder", field: "odometerKm", labelKey: "workOrder.odometerKm", defaultRequired: true, locked: true },
  { entity: "workOrder", field: "expectedReadyAt", labelKey: "workOrder.expectedReadyAt", defaultRequired: true, locked: false },
  { entity: "workOrder", field: "customerRequest", labelKey: "workOrder.customerRequest", defaultRequired: false, locked: false },
  { entity: "workOrder", field: "mechanicId", labelKey: "workOrder.mechanic", defaultRequired: false, locked: false },
];

/** A workshop's choices: "customer.email" → true. Missing keys mean the default. */
export type RequiredFieldChoices = Record<string, boolean>;

const keyOf = (d: RequiredFieldDefinition) => `${d.entity}.${d.field}`;

export function isRequired(def: RequiredFieldDefinition, choices: RequiredFieldChoices = {}): boolean {
  if (def.locked) return true;
  const choice = choices[keyOf(def)];
  return typeof choice === "boolean" ? choice : def.defaultRequired;
}

export function requiredFieldKey(def: RequiredFieldDefinition): string {
  return keyOf(def);
}

const filled = (v: unknown) => (typeof v === "string" ? v.trim() !== "" : v !== null && v !== undefined);

/**
 * What waits for the mechanic's intake ("Az átvételt a szerelő végzi", owner 2026-11-04): while it is pending the
 * work order needs only customer, vehicle and the request; the odometer reading comes with the intake.
 */
export const INTAKE_FIELDS: readonly string[] = ["odometerKm"];

/** The definitions of required fields that are still empty in the input. */
export function missingRequired(
  entity: RequiredEntity,
  input: CustomerInput | VehicleInput | WorkOrderInput,
  choices: RequiredFieldChoices = {},
  options: { intakePending?: boolean } = {},
): RequiredFieldDefinition[] {
  const values = input as unknown as Record<string, unknown>;
  return REQUIRED_FIELDS.filter((d) => d.entity === entity && isRequired(d, choices))
    .filter((d) => !(options.intakePending && entity === "workOrder" && INTAKE_FIELDS.includes(d.field)))
    .filter((d) =>
    d.field === "phoneAny" ? !filled(values.phone) && !filled(values.mobile) : !filled(values[d.field]),
  );
}

/** Keeps only known, non-locked keys with boolean values (settings come from the database). */
export function cleanRequiredChoices(value: unknown): RequiredFieldChoices {
  const out: RequiredFieldChoices = {};
  if (!value || typeof value !== "object") return out;
  for (const d of REQUIRED_FIELDS) {
    const v = (value as Record<string, unknown>)[keyOf(d)];
    if (!d.locked && typeof v === "boolean") out[keyOf(d)] = v;
  }
  return out;
}
